/* --------------------------------------------------------------------------
   useTankStats.js – derive tank counts & list filtered by Operator + Chemical

   Depends on:
   • Vuex containers module – action `containers/loadContainersStatusV2`
     which populates `getContainersV2` getter with a light payload that
     already includes `containerStatus` and `containerProperties`.
   • Reactive selection state from useForecastFilters.js (operatorId & chemical)

   Exports
     – loading (ref)
     – totalTanks (computed)
     – criticalTanks (computed)
     – rows (computed)  → array ready for CriticalTanksTable
--------------------------------------------------------------------------- */

import { ref, computed, watch } from 'vue';
import store from '@/store';
import selectionState from './useForecastFilters.js';

// const CORE_API = 'https://r3backendprd.azurewebsites.net/api';
const CORE_API = process.env.VUE_APP_API_BASE; //'https://r3backendstg.azurewebsites.net/api';

// ---------------------------------------------------- $-----------------------
// Memoisation helpers so that the /containers/compact/1 endpoint is fetched
// at most once per browser session *even if* multiple components import and
// call useTankStats() concurrently.
// ---------------------------------------------------------------------------

let containerCache = null;      // resolved data
let fetchPromise = null;      // in-flight request (if any)

const loading = ref(false);

async function fetchContainers() {
  // 1. Already have data – return synchronously.
  if (containerCache) return Promise.resolve(containerCache);

  // 2. Another watcher/component already kicked off the fetch – use the same promise.
  if (fetchPromise) return fetchPromise;

  loading.value = true;
  fetchPromise = (async () => {
    try {
      const token = store.getters['auth/getToken'];
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      const resp = await fetch(`${CORE_API}/containers/compact/1`, { headers });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      // Some endpoints wrap payload under a 'data' key; unwrap if needed
      const payload = Array.isArray(json) ? json : json?.data ?? [];

      if (!Array.isArray(payload)) {
        console.error('[useTankStats] Unexpected containers payload', json);
        throw new Error('Bad containers payload');
      }

      containerCache = payload;
      allContainers.value = payload;
      return payload;
    } finally {
      loading.value = false;
      fetchPromise = null; // allow GC and future refreshes if needed
    }
  })();

  return fetchPromise;
}

// reactive wrapper around the async data – starts empty, populated only once
const allContainers = ref([]);

// ---------------------------------------------------------------------------
// Delay the expensive /containers/compact/1 call until BOTH operator & chemical
// have been chosen.  We watch the two reactive fields together; when both are
// truthy and the cache is still empty we fire the network request.
// ---------------------------------------------------------------------------

watch(
  () => [selectionState.operatorId, selectionState.chemical],
  async ([opId, chem]) => {
    if (!opId || !chem) return;           // wait until both selected
    if (containerCache) {
      allContainers.value = containerCache;
      return;
    }

    const data = await fetchContainers();
    allContainers.value = data;
  },
  { immediate: true }
);

// ---------------------------------------------------------------------------
// helper: safely retrieve `propName` from the various shapes returned by the
// backend. We currently see three variants:
//   1️⃣  Array  → [{ propertyName, propValue, … }, …]
//   2️⃣  Dict   → { "Volume (%)": { propValue: 30, …}, … }
//   3️⃣  Dict   → { "Volume (%)": 30, … }   (flattened already)
// Comparison is case-insensitive.
// ---------------------------------------------------------------------------

function getProp(container, propName) {
  const sources = [
    container.properties,
    container.containerProperties,
    container.containerPropActuals,
  ];

  // Find the first non-empty source
  let raw = sources.find(s => {
    if (!s) return false;
    return Array.isArray(s) ? s.length > 0 : Object.keys(s).length > 0;
  });
  if (!raw) raw = [];

  const target = propName.toString().toLowerCase();

  // ----- Variant 1: array of objects --------------------------------------
  if (Array.isArray(raw)) {
    const match = raw.find(p => {
      const name = p.propertyName ?? p.property_name;
      return name && name.toString().toLowerCase() === target;
    });
    if (match) return match.propValue ?? match.prop_value ?? null;
  }

  // ----- Variant 2/3: dictionary ------------------------------------------
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    for (const [key, val] of Object.entries(raw)) {
      if (key.toString().toLowerCase() !== target) continue;

      // variant 2 – nested object with propValue
      if (val && typeof val === 'object') {
        return val.propValue ?? val.prop_value ?? null;
      }
      // variant 3 – primitive already
      return val;
    }
  }

  return null;
}

// filtered subset matches current operator & chemical selections
const operatorName = computed(() => {
  const opId = selectionState.operatorId;

  // the operators Vuex getter occasionally returns an object keyed by id
  // rather than a plain array – normalise to an array so we can safely
  // use Array.prototype.find regardless of shape
  const raw = store.getters['operators/getOperators'] || [];
  const arr = Array.isArray(raw) ? raw : Object.values(raw);

  const match = arr.find(o => o.operatorId === opId);
  return match ? match.operatorName : null;
});

const filtered = computed(() => {
  const operatorId = selectionState.operatorId;
  const chemical = selectionState.chemical;

  if (!operatorId || !chemical) return [];

  return (allContainers.value || []).filter(c => {
    // Operator match – prefer numeric id if present, else name
    const matchesOperator =
      (c.operatorId && String(c.operatorId) === String(operatorId)) ||
      (c.operator && operatorName.value && String(c.operator).toLowerCase() === String(operatorName.value).toLowerCase());

    if (!matchesOperator) return false;

    // Chemical match
    const productFields = [
      c.chemicalType,
      c.product,
      c.chemical,
      c.chemical_name,
      c.productName,
      c.product_name,
      getProp(c, 'Product'),
      getProp(c, 'Chemical'),
      getProp(c, 'Chemical Type'),
      getProp(c, 'product'),
      getProp(c, 'chemical'),
    ];

    const product = productFields.find(v => typeof v === 'string' && v.trim().length > 0);

    if (!product) return false;

    const chemLc = chemical.toString().trim().toLowerCase();
    const prodLc = product.toString().trim().toLowerCase();

    // exact match or product contains chemical (handles "Methanol (Anhydrous)")
    if (prodLc !== chemLc && !prodLc.includes(chemLc)) return false;

    // Scope match
    const scope = selectionState.selectedScope;
    if (scope && scope.type !== 'operator' && scope.id) {
      const targetId = String(scope.id);

      if (scope.type === 'area') {
        // Check areaId, area_id
        const areaId = c.areaId ?? c.area_id;
        if (areaId) {
          if (String(areaId) !== targetId) return false;
        } else {
          // If no ID on container, we can't match by ID. 
          // If the user selected a specific Area, we should exclude "unmapped" tanks.
          return false;
        }
      }

      if (scope.type === 'office') {
        const officeId = c.officeId ?? c.office_id;
        if (officeId) {
          if (String(officeId) !== targetId) return false;
        } else {
          return false;
        }
      }

      if (scope.type === 'district') {
        const districtId = c.districtId ?? c.district_id;
        if (districtId) {
          if (String(districtId) !== targetId) return false;
        } else {
          return false;
        }
      }
    }

    return true;
  });
});

const totalTanks = computed(() => filtered.value.length);

const criticalTanks = computed(() =>
  filtered.value.filter(c => {
    const statusRaw = c.containerStatus ?? c.status ?? '';
    const status = statusRaw.toString().trim();
    return (
      status === '❌' ||
      status.toLowerCase() === 'critical' ||
      status.toLowerCase() === 'crit' ||
      status.toLowerCase() === 'crt'
    );
  }).length
);

// rows for DataGrid
const rows = computed(() =>
  filtered.value.map(c => {
    const volPct = getProp(c, 'Volume (%)') ?? getProp(c, 'Volume') ?? getProp(c, 'Current Volume (%)');

    // Attempt to retrieve current & total volume in litres
    let currVolL =
      getProp(c, 'Volume (L)') ??
      getProp(c, 'Volume L') ??
      getProp(c, 'Current Volume (L)') ??
      getProp(c, 'Current Volume L') ??
      getProp(c, 'Current Volume Litres') ??
      null;

    if (typeof currVolL === 'string') {
      currVolL = Number(currVolL.replace(/[^0-9.]+/g, ''));
    }

    if (Number.isNaN(currVolL)) currVolL = null;

    let totalVolL =
      getProp(c, 'Capacity (L)') ??
      getProp(c, 'Capacity L') ??
      getProp(c, 'Tank Capacity (L)') ??
      getProp(c, 'Total Volume') ??
      getProp(c, 'Total Capacity') ??
      null;

    // ------------------------------------------------------------------
    // If total volume is absent but we have both current litres and
    // percentage, derive it:   total = current_L / (pct/100)
    // Example: 200 L at 50 %  ⇒ total = 200 / 0.5 = 400 L
    // ------------------------------------------------------------------

    if (totalVolL == null && currVolL != null && volPct != null) {
      let pctNum = volPct;

      if (typeof pctNum === 'string') {
        pctNum = pctNum.replace('%', '').trim();
      }

      pctNum = Number(pctNum);

      if (!Number.isNaN(pctNum) && pctNum > 0) {
        // Convert to decimal fraction if given as percent 0–100
        const ratio = pctNum > 1 ? pctNum / 100 : pctNum;
        if (ratio > 0) {
          totalVolL = currVolL / ratio;
        }
      }
    }

    if (totalVolL != null && !Number.isNaN(totalVolL)) {
      totalVolL = Math.round(totalVolL);
    }
    const dte = getProp(c, 'DTE');
    // compute critical date: if dte is numeric, add days to today
    let criticalBy = '-';
    if (dte !== null && !isNaN(dte)) {
      const date = new Date();
      date.setDate(date.getDate() + Number(dte));
      criticalBy = date.toLocaleDateString();
    }

    return {
      id: c.containerId,
      name: c.containerName || c.surface || c.name || `Tank-${c.containerId}`,
      location: c.area || c.location || '-',
      current: volPct !== null ? volPct : '-',
      currentL: currVolL,
      totalL: totalVolL,
      dte: dte !== null ? dte : '-',
      criticalBy,
      critical: (() => {
        const statusRaw = c.containerStatus ?? c.status ?? '';
        const status = statusRaw.toString().trim();
        return (
          status === '❌' ||
          status.toLowerCase() === 'critical' ||
          status.toLowerCase() === 'crit' ||
          status.toLowerCase() === 'crt'
        );
      })(),
    };
  })
);

// Sum of total volumes across filtered tanks (litres). Returns 0 when none.
const totalVolumeL = computed(() =>
  rows.value.reduce((acc, r) => {
    const v = typeof r.totalL === 'string' ? Number(r.totalL) : r.totalL;
    return acc + (Number.isFinite(v) ? v : 0);
  }, 0)
);

export default function useTankStats() {
  return {
    loading,
    totalTanks,
    criticalTanks,
    rows,
    totalVolumeL,
  };
}
